import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Brain, Users, ChevronLeft, Phone, Mail, FileText, MessageSquare,
  Database, Eye, Settings, Clock, CheckCircle2, XCircle, Loader2,
  Plus, Send, RefreshCw, AlertTriangle, ChevronRight, Sparkles,
  Edit2, Save, Trash2, Shield, Zap, Activity, ArrowRightLeft,
  Lock, ToggleLeft, ToggleRight, User, Mic, ThumbsUp, ThumbsDown,
  Info, ChevronDown
} from 'lucide-react';
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl();

// Source configs for visual display
const SOURCE_DEFS = {
  ringover:     { label: "Ringover",      icon: Phone,          color: "from-blue-500 to-blue-600",    side: "input" },
  email:        { label: "Emails",        icon: Mail,           color: "from-amber-500 to-orange-500", side: "input" },
  crm_pipeline: { label: "CRM Pipeline",  icon: Database,       color: "from-emerald-500 to-green-600",side: "input" },
  whatsapp:     { label: "WhatsApp",      icon: MessageSquare,  color: "from-green-500 to-green-600",  side: "input" },
  whatsapp_group: { label: "Agent SAN-IA WhatsApp Groupes", icon: Users, color: "from-teal-500 to-cyan-600", side: "input" },
  google_drive: { label: "Google Drive",  icon: FileText,       color: "from-yellow-500 to-amber-500", side: "input" },
  manual_notes: { label: "Notes manuelles", icon: Edit2,        color: "from-violet-500 to-purple-500",side: "input" },
  google_ads_exclusions: { label: "Google Ads Exclusions", icon: Shield, color: "from-red-500 to-rose-600", side: "input" },
};

const OUTPUT_TOOLS = [
  { id: "email_send",  label: "Email (envoi)",  icon: Mail,          color: "from-amber-500 to-orange-500" },
  { id: "sms",         label: "SMS",            icon: Phone,         color: "from-blue-500 to-blue-600" },
  { id: "whatsapp_out",label: "WhatsApp",       icon: MessageSquare, color: "from-green-500 to-green-600" },
  { id: "voice",       label: "Voix",           icon: Zap,           color: "from-pink-500 to-rose-500" },
  { id: "pennylane",   label: "Pennylane",      icon: FileText,      color: "from-indigo-500 to-blue-600" },
];

const MEMORY_ICONS = {
  interaction: Phone,
  event: Activity,
  media: Eye,
  knowledge: Brain,
  thought: Edit2,
};

const MEMORY_COLORS = {
  ringover: "text-blue-400",
  email: "text-amber-400",
  crm_pipeline: "text-emerald-400",
  whatsapp: "text-green-400",
  whatsapp_group: "text-teal-400",
  google_drive: "text-yellow-400",
  manual: "text-violet-400",
  ai_extracted: "text-pink-400",
  google_ads_exclusions: "text-red-400",
};

// ═══════════════════════════════════════
// HELPER: authFetch
// ═══════════════════════════════════════
const authFetch = async (url, options = {}) => {
  const res = await fetch(url, { ...options, credentials: 'include' });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
};


// ═══════════════════════════════════════
// PERSON CARD
// ═══════════════════════════════════════
const PersonCard = ({ person, onClick }) => {
  const activeSources = (person.sources_config || []).filter(s => s.enabled).length;
  const initial = (person.prenom || person.name || '?')[0].toUpperCase();

  return (
    <button data-testid={`clone-card-${person.person_id}`} onClick={() => onClick(person)}
      className="group relative bg-slate-800/60 border border-slate-700/60 rounded-xl p-5 text-left hover:border-blue-500/40 hover:bg-slate-800/80 transition-all duration-300 w-full"
    >
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-violet-500/20 border border-blue-500/30 flex items-center justify-center text-xl font-bold text-blue-400 group-hover:scale-105 transition-transform">
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-white truncate">{person.name}</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            {(person.contexts || []).map(c => c.role).join(', ') || person.type}
          </p>
          <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
            <span className="flex items-center gap-1"><Database size={10} /> {person.stats?.total_memories || 0} memoires</span>
            <span className="flex items-center gap-1"><Zap size={10} /> {activeSources} sources</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <span className={`px-2 py-0.5 rounded text-[10px] border ${
            person.type === 'personnel' ? 'bg-violet-500/15 text-violet-400 border-violet-500/30' : 'bg-blue-500/15 text-blue-400 border-blue-500/30'
          }`}>
            {person.type === 'personnel' ? 'Personnel' : 'Collaborateur'}
          </span>
          <span className={`w-2 h-2 rounded-full ${person.clonable ? 'bg-emerald-400' : 'bg-slate-600'}`} />
        </div>
      </div>
      <ChevronRight size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600 group-hover:text-blue-400 transition-colors" />
    </button>
  );
};


// ═══════════════════════════════════════
// TAB: SOURCES (brain visual)
// ═══════════════════════════════════════
const SYNCABLE_SOURCES = new Set(["ringover", "email", "crm_pipeline"]);


const SYNC_INTERVAL_OPTIONS = [
  { value: 1, label: '1h' },
  { value: 2, label: '2h' },
  { value: 3, label: '3h' },
  { value: 6, label: '6h' },
  { value: 12, label: '12h' },
  { value: 24, label: '24h' },
];

const AutoSyncConfig = ({ person, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const autoEnabled = person.sync_auto_enabled !== false;
  const interval = person.sync_interval_hours || 3;

  const updateConfig = async (updates) => {
    setLoading(true);
    try {
      await authFetch(`${API}/api/clones/persons/${person.person_id}/sync-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      onUpdate(updates);
    } catch (err) {
      console.error('Sync config error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="auto-sync-config" className="flex items-center gap-2">
      <button
        data-testid="auto-sync-toggle"
        onClick={() => updateConfig({ sync_auto_enabled: !autoEnabled })}
        disabled={loading}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs border transition-all ${
          autoEnabled
            ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
            : 'bg-slate-800 border-slate-700 text-slate-500'
        }`}
      >
        <Clock size={12} />
        Auto
        {autoEnabled
          ? <ToggleRight size={14} className="text-emerald-400" />
          : <ToggleLeft size={14} className="text-slate-600" />
        }
      </button>
      {autoEnabled && (
        <select
          data-testid="sync-interval-select"
          value={interval}
          onChange={(e) => updateConfig({ sync_interval_hours: Number(e.target.value) })}
          disabled={loading}
          className="bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-lg px-2 py-1.5 focus:border-blue-500 focus:outline-none"
        >
          {SYNC_INTERVAL_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>Toutes les {opt.label}</option>
          ))}
        </select>
      )}
    </div>
  );
};

const SourcesTab = ({ person, onUpdate, onRefresh }) => {
  const [syncing, setSyncing] = useState({});
  const [syncResults, setSyncResults] = useState({});
  const [syncAllLoading, setSyncAllLoading] = useState(false);
  const [resyncLoading, setResyncLoading] = useState({});

  const toggleSource = (sourceType) => {
    const updated = (person.sources_config || []).map(s =>
      s.type === sourceType ? { ...s, enabled: !s.enabled } : s
    );
    onUpdate({ sources_config: updated });
  };

  const syncSource = async (sourceType) => {
    setSyncing(prev => ({ ...prev, [sourceType]: true }));
    setSyncResults(prev => ({ ...prev, [sourceType]: null }));
    try {
      const data = await authFetch(`${API}/api/clones/persons/${person.person_id}/sources/${sourceType}/sync`, {
        method: 'POST',
      });
      setSyncResults(prev => ({ ...prev, [sourceType]: data }));
      onRefresh();
    } catch (err) {
      setSyncResults(prev => ({ ...prev, [sourceType]: { success: false, error: err.message } }));
    } finally {
      setSyncing(prev => ({ ...prev, [sourceType]: false }));
    }
  };

  const syncAll = async () => {
    setSyncAllLoading(true);
    setSyncResults({});
    try {
      const data = await authFetch(`${API}/api/clones/persons/${person.person_id}/sources/sync-all`, {
        method: 'POST',
      });
      setSyncResults(data.results || {});
      onRefresh();
    } catch (err) {
      console.error('Sync all error:', err);
    } finally {
      setSyncAllLoading(false);
    }
  };

  const resyncSource = async (sourceType) => {
    if (!window.confirm(`Re-synchroniser ${sourceType} ? Cela va supprimer les anciennes memoires de cette source et les re-importer avec les dernieres ameliorations.`)) return;
    setResyncLoading(prev => ({ ...prev, [sourceType]: true }));
    setSyncResults(prev => ({ ...prev, [sourceType]: null }));
    try {
      const data = await authFetch(`${API}/api/clones/persons/${person.person_id}/sources/${sourceType}/resync`, {
        method: 'POST',
      });
      setSyncResults(prev => ({ ...prev, [sourceType]: { ...data, resync: true } }));
      onRefresh();
    } catch (err) {
      setSyncResults(prev => ({ ...prev, [sourceType]: { success: false, error: err.message } }));
    } finally {
      setResyncLoading(prev => ({ ...prev, [sourceType]: false }));
    }
  };

  const inputSources = (person.sources_config || []).filter(s => SOURCE_DEFS[s.type]);

  return (
    <div data-testid="sources-tab" className="space-y-6">
      {/* Brain visual */}
      <div className="relative bg-slate-900/50 rounded-xl border border-slate-700/50 p-8">
        <div className="grid grid-cols-3 gap-6 items-center">
          {/* LEFT: Input sources */}
          <div className="space-y-3">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Entrees (apprentissage)</h4>
            {inputSources.map(src => {
              const def = SOURCE_DEFS[src.type];
              const Icon = def.icon;
              return (
                <button key={src.type} data-testid={`source-toggle-${src.type}`} onClick={() => toggleSource(src.type)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all ${
                    src.enabled
                      ? 'bg-slate-800 border-slate-600 hover:border-slate-500'
                      : 'bg-slate-800/40 border-slate-700/40 opacity-50 hover:opacity-70'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${def.color} flex items-center justify-center`}>
                    <Icon size={14} className="text-white" />
                  </div>
                  <span className="text-sm text-white flex-1 text-left">{def.label}</span>
                  {src.enabled
                    ? <ToggleRight size={20} className="text-emerald-400" />
                    : <ToggleLeft size={20} className="text-slate-600" />
                  }
                </button>
              );
            })}
          </div>

          {/* CENTER: Avatar / Brain */}
          <div className="flex flex-col items-center">
            <div className="relative">
              <div className="absolute inset-0 -m-3 rounded-full border border-blue-500/20 animate-pulse" />
              <div className="absolute inset-0 -m-6 rounded-full border border-violet-500/10" />
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-blue-500/20 via-violet-500/20 to-pink-500/20 border-2 border-blue-500/40 flex items-center justify-center">
                <Brain size={40} className="text-blue-400" />
              </div>
            </div>
            <h3 className="text-lg font-bold text-white mt-4">{person.name}</h3>
            <p className="text-xs text-slate-400">
              {(person.contexts || []).map(c => c.role).join(', ')}
            </p>
            <div className="flex gap-2 mt-3 text-[10px]">
              <span className="px-2 py-0.5 rounded bg-blue-500/15 text-blue-400 border border-blue-500/30">
                {person.stats?.total_memories || 0} memoires
              </span>
              <span className="px-2 py-0.5 rounded bg-slate-700 text-slate-400">
                Autonomie: {person.agent_config?.autonomy_level || 0}
              </span>
            </div>
          </div>

          {/* RIGHT: Output tools (locked for Phase 2) */}
          <div className="space-y-3">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Sorties (actions)</h4>
            {OUTPUT_TOOLS.map(tool => (
              <div key={tool.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-700/30 bg-slate-800/30 opacity-40">
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${tool.color} flex items-center justify-center`}>
                  <tool.icon size={14} className="text-white" />
                </div>
                <span className="text-sm text-slate-400 flex-1">{tool.label}</span>
                <Lock size={14} className="text-slate-600" />
              </div>
            ))}
            <p className="text-[10px] text-slate-600 text-center mt-2">Phase 2 — autonomie requise</p>
          </div>
        </div>
      </div>

      {/* Sync controls */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-white flex items-center gap-2">
            <RefreshCw size={14} className="text-blue-400" /> Ingestion des donnees
          </h4>
          <div className="flex items-center gap-3">
            <AutoSyncConfig person={person} onUpdate={onUpdate} />
            <button data-testid="sync-all-btn" onClick={syncAll} disabled={syncAllLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-500 disabled:opacity-50 transition-colors"
            >
              {syncAllLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Synchroniser tout
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {inputSources.filter(s => s.enabled && SYNCABLE_SOURCES.has(s.type)).map(src => {
            const def = SOURCE_DEFS[src.type];
            const Icon = def.icon;
            const isSyncing = syncing[src.type];
            const result = syncResults[src.type];
            return (
              <div key={src.type} data-testid={`sync-row-${src.type}`}
                className="flex items-center gap-3 px-4 py-3 bg-slate-900/50 rounded-lg border border-slate-700/30"
              >
                <div className={`w-7 h-7 rounded-md bg-gradient-to-br ${def.color} flex items-center justify-center`}>
                  <Icon size={12} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white">{def.label}</p>
                  <p className="text-[10px] text-slate-500">
                    {src.last_sync
                      ? `Derniere sync: ${new Date(src.last_sync).toLocaleString('fr-FR')}`
                      : 'Jamais synchronise'}
                  </p>
                </div>
                {result && (
                  <div className="text-[10px] text-right mr-2">
                    {result.success !== false ? (
                      <span className="text-emerald-400">
                        {result.resync && <span className="text-amber-400 mr-1">purge:{result.purged}</span>}
                        +{result.total_new || (result.calls || 0) + (result.sms || 0)} memoires
                        {result.updated > 0 && <span className="text-amber-400 ml-1">({result.updated} mises a jour)</span>}
                        {result.calls !== undefined && <span className="text-slate-500 ml-1">({result.calls} appels, {result.sms} sms)</span>}
                      </span>
                    ) : (
                      <span className="text-red-400">{result.error || 'Erreur'}</span>
                    )}
                  </div>
                )}
                <button data-testid={`resync-btn-${src.type}`} onClick={() => resyncSource(src.type)} disabled={resyncLoading[src.type] || isSyncing}
                  className="flex items-center gap-1 px-2 py-1.5 bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 rounded-md text-[10px] disabled:opacity-50 transition-colors border border-amber-600/20"
                  title="Re-sync: purge + re-import"
                >
                  {resyncLoading[src.type] ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                  Re
                </button>
                <button data-testid={`sync-btn-${src.type}`} onClick={() => syncSource(src.type)} disabled={isSyncing}
                  className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-md text-xs disabled:opacity-50 transition-colors"
                >
                  {isSyncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                  Sync
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Transcription section */}
      <TranscriptionPanel person={person} onRefresh={onRefresh} />
    </div>
  );
};

const TranscriptionPanel = ({ person, onRefresh }) => {
  const [status, setStatus] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const pollingRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const data = await authFetch(`${API}/api/clones/persons/${person.person_id}/transcribe/status`);
      setStatus(data);
      if (data.task_running) {
        setTranscribing(true);
      } else if (transcribing && !data.task_running) {
        setTranscribing(false);
        if (onRefresh) onRefresh();
      }
      return data;
    } catch (err) {
      console.error('Transcription status error:', err);
      return null;
    }
  }, [person.person_id, transcribing, onRefresh]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Poll while transcribing
  useEffect(() => {
    if (transcribing) {
      pollingRef.current = setInterval(() => loadStatus(), 5000);
      return () => clearInterval(pollingRef.current);
    } else {
      if (pollingRef.current) clearInterval(pollingRef.current);
    }
  }, [transcribing, loadStatus]);

  const startTranscribe = async (batchSize = 5, loopAll = false) => {
    setTranscribing(true);
    try {
      await authFetch(`${API}/api/clones/persons/${person.person_id}/transcribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batch_size: batchSize, loop_all: loopAll }),
      });
    } catch (err) {
      console.error('Start transcription error:', err);
      setTranscribing(false);
    }
  };

  const retryFailed = async () => {
    setRetrying(true);
    try {
      await authFetch(`${API}/api/clones/persons/${person.person_id}/transcribe/retry-failed`, { method: 'POST' });
      await loadStatus();
    } catch (err) {
      console.error('Retry failed error:', err);
    } finally {
      setRetrying(false);
    }
  };

  const resetTask = async () => {
    try {
      await authFetch(`${API}/api/clones/persons/${person.person_id}/transcribe/reset`, { method: 'POST' });
      setTranscribing(false);
      await loadStatus();
    } catch (err) {
      console.error('Reset task error:', err);
    }
  };

  if (!status) return null;
  if (status.with_recording === 0) return null;

  const pct = status.percent || 0;
  const hasFailed = (status.failed || 0) > 0;
  const stoppedReason = status.stopped_reason || '';

  return (
    <div data-testid="transcription-panel" className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-white flex items-center gap-2">
          <Mic size={14} className="text-violet-400" /> Transcription Whisper
        </h4>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500">
            {status.transcribed}/{status.with_recording} transcrits ({pct}%)
            {hasFailed && <span className="text-red-400 ml-1">| {status.failed} echoues</span>}
          </span>
          {status.remaining > 0 && !transcribing && (
            <>
              <button data-testid="start-transcribe-all" onClick={() => startTranscribe(5, true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white rounded-lg text-xs hover:bg-violet-500 transition-colors"
              >
                <Mic size={12} /> Tout transcrire ({status.remaining})
              </button>
              <button data-testid="start-transcribe-5" onClick={() => startTranscribe(5)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-violet-600/50 text-white rounded-lg text-[10px] hover:bg-violet-500 transition-colors"
              >
                5 seuls
              </button>
            </>
          )}
          {transcribing && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600/30 text-violet-400 rounded-lg text-xs border border-violet-500/30">
              <Loader2 size={12} className="animate-spin" />
              En cours... {status.batch_done || 0} fait{status.last_contact ? ` (${status.last_contact})` : ''}
            </span>
          )}
          {transcribing && (
            <button data-testid="reset-transcribe-task" onClick={resetTask}
              className="flex items-center gap-1 px-2 py-1.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-md text-[10px] border border-red-600/20 transition-colors"
              title="Forcer l'arret de la tache"
            >
              <XCircle size={10} /> Stop
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-slate-900 rounded-full h-2">
        <div className="bg-gradient-to-r from-violet-500 to-blue-500 h-2 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Stopped reason alert */}
      {stoppedReason && !transcribing && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <AlertTriangle size={14} className="text-amber-400 shrink-0" />
          <p className="text-[11px] text-amber-300">
            {stoppedReason === 'too_many_consecutive_errors'
              ? 'Lot arrete : trop d\'erreurs consecutives. Verifiez le budget Universal Key ou la qualite des enregistrements.'
              : `Arret: ${stoppedReason}`
            }
          </p>
        </div>
      )}

      {/* Failed calls retry */}
      {hasFailed && !transcribing && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg">
          <XCircle size={14} className="text-red-400 shrink-0" />
          <p className="text-[11px] text-red-300 flex-1">
            {status.failed} transcription(s) echouee(s).
          </p>
          <button data-testid="retry-failed-btn" onClick={retryFailed} disabled={retrying}
            className="flex items-center gap-1 px-2.5 py-1 bg-amber-600/30 hover:bg-amber-600/40 text-amber-300 rounded-md text-[10px] border border-amber-600/20 transition-colors disabled:opacity-50"
          >
            {retrying ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
            Reinitialiser pour re-essayer
          </button>
        </div>
      )}

      <p className="text-[10px] text-slate-600">
        {status.remaining > 0
          ? `${status.remaining} enregistrements restants. Apres transcription, Re-sync Ringover pour mettre a jour les memoires.`
          : 'Toutes les transcriptions sont completes. Faites un Re-sync Ringover pour integrer les transcriptions dans les memoires.'
        }
      </p>
    </div>
  );
};


// ═══════════════════════════════════════
// TAB: BEHAVIOUR
// ═══════════════════════════════════════
const BehaviourTab = ({ person, onUpdate }) => {
  const [style, setStyle] = useState(person.behaviour?.style || '');
  const [rules, setRules] = useState(person.behaviour?.rules || []);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [defaultPrompt, setDefaultPrompt] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [newRule, setNewRule] = useState('');
  const [dirty, setDirty] = useState(false);
  const [promptDirty, setPromptDirty] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [promptLoaded, setPromptLoaded] = useState(false);

  // Load prompt from dedicated endpoint
  useEffect(() => {
    const loadPrompt = async () => {
      try {
        const data = await authFetch(`${API}/api/clones/persons/${person.person_id}/prompt`);
        setDefaultPrompt(data.default_prompt || '');
        if (data.is_custom && data.custom_prompt) {
          setSystemPrompt(data.custom_prompt);
          setIsCustom(true);
        } else {
          setSystemPrompt(data.default_prompt || '');
          setIsCustom(false);
        }
        setPromptLoaded(true);
      } catch (err) {
        console.error('Load prompt error:', err);
        setPromptLoaded(true);
      }
    };
    loadPrompt();
  }, [person.person_id]);

  const save = () => {
    onUpdate({
      behaviour: {
        ...person.behaviour,
        style,
        rules,
      }
    });
    setDirty(false);
  };

  const savePrompt = async () => {
    setSavingPrompt(true);
    try {
      await authFetch(`${API}/api/clones/persons/${person.person_id}/prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ system_prompt: systemPrompt }),
      });
      setIsCustom(true);
      setPromptDirty(false);
    } catch (err) {
      console.error('Save prompt error:', err);
    } finally {
      setSavingPrompt(false);
    }
  };

  const resetPrompt = async () => {
    if (!window.confirm('Reinitialiser le prompt vers la version par defaut ? Votre version personnalisee sera perdue.')) return;
    setSavingPrompt(true);
    try {
      const data = await authFetch(`${API}/api/clones/persons/${person.person_id}/prompt/reset`, { method: 'POST' });
      setSystemPrompt(data.default_prompt || defaultPrompt);
      setIsCustom(false);
      setPromptDirty(false);
    } catch (err) {
      console.error('Reset prompt error:', err);
    } finally {
      setSavingPrompt(false);
    }
  };

  const addRule = () => {
    if (newRule.trim()) {
      setRules([...rules, newRule.trim()]);
      setNewRule('');
      setDirty(true);
    }
  };

  const removeRule = (idx) => {
    setRules(rules.filter((_, i) => i !== idx));
    setDirty(true);
  };

  return (
    <div data-testid="behaviour-tab" className="space-y-6">
      {/* System Prompt — MAIN SECTION */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-white flex items-center gap-2">
            <Settings size={14} className="text-blue-400" /> Prompt systeme
            {isCustom && (
              <span className="px-2 py-0.5 rounded text-[10px] bg-blue-500/15 text-blue-400 border border-blue-500/30">
                Personnalise
              </span>
            )}
            {!isCustom && promptLoaded && (
              <span className="px-2 py-0.5 rounded text-[10px] bg-slate-600/30 text-slate-400 border border-slate-600/30">
                Par defaut
              </span>
            )}
          </h4>
          <div className="flex items-center gap-2">
            {isCustom && (
              <button data-testid="reset-prompt-btn" onClick={resetPrompt} disabled={savingPrompt}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 rounded-md text-[10px] border border-amber-600/20 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={10} /> Reinitialiser
              </button>
            )}
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">
          Ce prompt definit la personnalite et les instructions du clone. Variables disponibles : <code className="text-blue-400">{'{{person_name}}'}</code>, <code className="text-blue-400">{'{{role}}'}</code>, <code className="text-blue-400">{'{{style}}'}</code>, <code className="text-blue-400">{'{{rules}}'}</code>. Les souvenirs, statistiques et historique sont injectes automatiquement.
        </p>
        <textarea
          data-testid="system-prompt-textarea"
          value={systemPrompt}
          onChange={e => { setSystemPrompt(e.target.value); setPromptDirty(true); }}
          placeholder="Chargement du prompt..."
          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-xs text-slate-300 placeholder-slate-600 focus:border-blue-500 focus:outline-none resize-none font-mono leading-relaxed"
          style={{ minHeight: '280px' }}
        />
        {promptDirty && (
          <button data-testid="save-prompt-btn" onClick={savePrompt} disabled={savingPrompt}
            className="mt-3 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-500 disabled:opacity-50"
          >
            {savingPrompt ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Sauvegarder le prompt
          </button>
        )}
      </div>

      {/* Style */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5">
        <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
          <User size={14} className="text-blue-400" /> Style de communication
        </h4>
        <p className="text-[11px] text-slate-500 mb-2">
          Utilise dans le prompt via la variable <code className="text-blue-400">{'{{style}}'}</code>.
        </p>
        <textarea value={style} onChange={e => { setStyle(e.target.value); setDirty(true); }}
          placeholder="Ex: Professionnelle, directe, vouvoie toujours les clients..."
          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-sm text-slate-300 placeholder-slate-600 focus:border-blue-500 focus:outline-none resize-none h-24"
        />
      </div>

      {/* Rules */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5">
        <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
          <Shield size={14} className="text-amber-400" /> Regles & Limites
        </h4>
        <p className="text-[11px] text-slate-500 mb-3">
          Utilisees dans le prompt via la variable <code className="text-blue-400">{'{{rules}}'}</code>.
        </p>
        <div className="space-y-2">
          {rules.map((rule, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 bg-slate-900/50 rounded-lg border border-slate-700/30">
              <span className="text-xs text-slate-300 flex-1">{rule}</span>
              <button onClick={() => removeRule(i)} className="text-slate-500 hover:text-red-400"><Trash2 size={12} /></button>
            </div>
          ))}
          <div className="flex gap-2">
            <input type="text" value={newRule} onChange={e => setNewRule(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addRule()}
              placeholder="Ajouter une regle..."
              className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-300 placeholder-slate-600 focus:border-blue-500 focus:outline-none"
            />
            <button onClick={addRule} className="px-3 py-2 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-500">
              <Plus size={12} />
            </button>
          </div>
        </div>
      </div>

      {/* Patterns (read-only) */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5">
        <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
          <Sparkles size={14} className="text-violet-400" /> Patterns detectes
        </h4>
        {(person.behaviour?.patterns || []).length > 0 ? (
          <div className="space-y-1">
            {person.behaviour.patterns.map((p, i) => (
              <div key={i} className="text-xs text-slate-400 px-3 py-1.5 bg-violet-500/10 rounded">{p}</div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-500">Les patterns seront detectes automatiquement par l'IA a partir des interactions.</p>
        )}
      </div>

      {dirty && (
        <button data-testid="save-behaviour" onClick={save}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-500"
        >
          <Save size={14} /> Sauvegarder style & regles
        </button>
      )}
    </div>
  );
};


// ═══════════════════════════════════════
// TAB: TIMELINE
// ═══════════════════════════════════════
const TimelineTab = ({ person }) => {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [newThought, setNewThought] = useState('');
  const [adding, setAdding] = useState(false);

  const loadMemories = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/api/clones/persons/${person.person_id}/memories?limit=200`;
      if (filterType !== 'all') url += `&type=${filterType}`;
      if (filterSource !== 'all') url += `&source=${filterSource}`;
      const data = await authFetch(url);
      setMemories(data.memories || []);
    } catch (err) {
      console.error('Load memories error:', err);
    } finally {
      setLoading(false);
    }
  }, [person.person_id, filterType, filterSource]);

  useEffect(() => { loadMemories(); }, [loadMemories]);

  const addThought = async () => {
    if (!newThought.trim()) return;
    setAdding(true);
    try {
      await authFetch(`${API}/api/clones/persons/${person.person_id}/memories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'thought',
          source: 'manual',
          summary: newThought,
          memory_text: newThought,
        }),
      });
      setNewThought('');
      loadMemories();
    } catch (err) {
      console.error('Add thought error:', err);
    } finally {
      setAdding(false);
    }
  };

  // Filter by search query (client-side)
  const filteredMemories = searchQuery.trim()
    ? memories.filter(m => {
        const q = searchQuery.toLowerCase();
        return (m.summary || '').toLowerCase().includes(q) ||
               (m.content?.client_name || '').toLowerCase().includes(q) ||
               (m.content?.contact_name || '').toLowerCase().includes(q) ||
               (m.client_id || '').toLowerCase().includes(q) ||
               (m.tags || []).some(t => t.toLowerCase().includes(q));
      })
    : memories;

  // Group by date
  const grouped = {};
  filteredMemories.forEach(m => {
    const date = new Date(m.timestamp).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(m);
  });

  return (
    <div data-testid="timeline-tab" className="space-y-4">
      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <select data-testid="timeline-filter-type" value={filterType} onChange={e => setFilterType(e.target.value)}
          className="bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-md px-2 py-1.5 focus:border-blue-500 focus:outline-none"
        >
          <option value="all">Tous types</option>
          <option value="interaction">Interactions</option>
          <option value="event">Evenements</option>
          <option value="knowledge">Connaissances</option>
          <option value="thought">Pensees</option>
          <option value="media">Media</option>
        </select>
        <select data-testid="timeline-filter-source" value={filterSource} onChange={e => setFilterSource(e.target.value)}
          className="bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-md px-2 py-1.5 focus:border-blue-500 focus:outline-none"
        >
          <option value="all">Toutes sources</option>
          <option value="ringover">Ringover</option>
          <option value="email">Email</option>
          <option value="crm_pipeline">CRM Pipeline</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="manual">Manuel</option>
        </select>
        <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          placeholder="Rechercher client ou mot-cle..."
          data-testid="timeline-search"
          className="bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-md px-2 py-1.5 focus:border-blue-500 focus:outline-none flex-1 max-w-[220px]"
        />
        <button onClick={loadMemories} className="text-slate-400 hover:text-white"><RefreshCw size={14} /></button>
      </div>

      {/* Add thought */}
      <div className="flex gap-2">
        <input type="text" value={newThought} onChange={e => setNewThought(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addThought()}
          placeholder="Ajouter une pensee ou note..."
          className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-300 placeholder-slate-500 focus:border-blue-500 focus:outline-none"
        />
        <button data-testid="add-thought-btn" onClick={addThought} disabled={adding || !newThought.trim()}
          className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm hover:bg-violet-500 disabled:opacity-50 flex items-center gap-1"
        >
          {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Pensee
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-blue-400" /></div>
      ) : filteredMemories.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Brain size={32} className="mx-auto mb-3 text-slate-600" />
          <p>{searchQuery ? `Aucun resultat pour "${searchQuery}"` : 'Aucune memoire. Les souvenirs apparaitront apres la premiere ingestion.'}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([date, mems]) => (
            <div key={date}>
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 sticky top-0 bg-slate-900/80 py-1">{date}</h4>
              <div className="space-y-1.5 border-l-2 border-slate-700/50 ml-3 pl-4">
                {mems.map(m => {
                  const Icon = MEMORY_ICONS[m.type] || Activity;
                  const colorClass = MEMORY_COLORS[m.source] || "text-slate-400";
                  return (
                    <div key={m.memory_id} className="flex items-start gap-3 py-2 px-3 rounded-lg hover:bg-slate-800/40 transition-colors group">
                      <div className={`mt-0.5 ${colorClass}`}><Icon size={14} /></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-300">{m.summary}</p>
                        <div className="flex gap-2 mt-1 text-[10px] text-slate-500">
                          <span>{new Date(m.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                          <span className="px-1 rounded bg-slate-800">{m.source}</span>
                          <span className="px-1 rounded bg-slate-800">{m.type}</span>
                          {(m.content?.client_name || m.client_id) && (
                            <span className="px-1 rounded bg-emerald-500/10 text-emerald-400">
                              {m.content?.client_name || m.client_id}
                            </span>
                          )}
                          {m.importance === 'high' && (
                            <span className="px-1 rounded bg-amber-500/10 text-amber-400">important</span>
                          )}
                        </div>
                      </div>
                      {m.tags && m.tags.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {m.tags.slice(0, 3).map(t => (
                            <span key={t} className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400">{t}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════
// TAB: CHAT
// ═══════════════════════════════════════
// TTS Button — plays clone voice for a message
const TTSButton = ({ personId, text }) => {
  const [state, setState] = useState('idle'); // idle | loading | playing
  const audioRef = useRef(null);

  const play = async () => {
    if (state === 'playing' && audioRef.current) {
      audioRef.current.pause();
      setState('idle');
      return;
    }
    setState('loading');
    try {
      const resp = await fetch(`${API}/api/clones/persons/${personId}/voice/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: text.slice(0, 2000) }),
      });
      if (!resp.ok) {
        setState('idle');
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setState('idle');
      audio.play();
      setState('playing');
    } catch {
      setState('idle');
    }
  };

  return (
    <button
      data-testid="tts-listen-btn"
      onClick={play}
      className={`p-1 rounded transition-colors ${
        state === 'playing'
          ? 'text-blue-400 bg-blue-500/15'
          : state === 'loading'
            ? 'text-slate-500 cursor-wait'
            : 'text-slate-500 hover:text-blue-400 hover:bg-blue-500/10'
      }`}
      title="Ecouter"
    >
      {state === 'loading' ? <Loader2 size={13} className="animate-spin" /> : <Mic size={13} />}
    </button>
  );
};


const INTENT_LABELS = {
  GENERAL: { label: "Général", color: "text-slate-400 bg-slate-600/20 border-slate-600/30" },
  DETAIL: { label: "Détail", color: "text-amber-400 bg-amber-600/15 border-amber-500/30" },
  FULL: { label: "Complet", color: "text-violet-400 bg-violet-600/15 border-violet-500/30" },
};

const SOURCE_LABELS = {
  ringover: { label: "Ringover", icon: "phone" },
  email: { label: "Email", icon: "mail" },
  crm_pipeline: { label: "CRM", icon: "database" },
};

const ChatTab = ({ person }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const data = await authFetch(`${API}/api/clones/persons/${person.person_id}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: input,
          conversation_history: messages.slice(-8).map(m => ({ role: m.role, content: m.content })),
        }),
      });
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        log_id: data.log_id,
        memories_used: data.memories_used,
        retrieval_info: data.retrieval_info,
        feedback: null,
      }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'error', content: `Erreur: ${err.message}` }]);
    } finally {
      setLoading(false);
    }
  };

  const submitFeedback = async (msgIdx, feedback) => {
    const msg = messages[msgIdx];
    if (!msg?.log_id) return;

    try {
      await authFetch(`${API}/api/clones/persons/${person.person_id}/chat/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ log_id: msg.log_id, feedback }),
      });
      setMessages(prev => prev.map((m, i) =>
        i === msgIdx ? { ...m, feedback } : m
      ));
    } catch (err) {
      console.error('Feedback error:', err);
    }
  };

  return (
    <div data-testid="chat-tab" className="flex flex-col h-[600px]">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 p-4">
        {messages.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Brain size={32} className="mx-auto mb-3 text-blue-400/40" />
            <p className="text-sm">Interrogez la memoire de {person.name}</p>
            <p className="text-xs mt-1">Ex: "Qu'a fait {person.prenom} sur le dossier Benhamou cette semaine ?"</p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] ${
              msg.role === 'user'
                ? 'bg-blue-600 text-white rounded-xl px-4 py-3'
                : msg.role === 'error'
                  ? 'bg-red-500/20 border border-red-500/30 text-red-300 rounded-xl px-4 py-3'
                  : 'space-y-0'
            }`}>
              {msg.role === 'user' ? (
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              ) : msg.role === 'error' ? (
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              ) : (
                <>
                  {/* Response bubble */}
                  <div className="bg-slate-800 border border-slate-700/50 text-slate-300 rounded-xl px-4 py-3">
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>

                  {/* Retrieval trace bar */}
                  {msg.retrieval_info && (
                    <RetrievalTrace info={msg.retrieval_info} />
                  )}

                  {/* Feedback + Listen buttons */}
                  <div className="flex items-center gap-1.5 mt-1 ml-1">
                    <button
                      data-testid={`feedback-positive-${i}`}
                      onClick={() => submitFeedback(i, 'positive')}
                      disabled={msg.feedback !== null}
                      className={`p-1 rounded transition-colors ${
                        msg.feedback === 'positive'
                          ? 'text-emerald-400 bg-emerald-500/15'
                          : msg.feedback === 'negative'
                            ? 'text-slate-600 cursor-not-allowed'
                            : 'text-slate-500 hover:text-emerald-400 hover:bg-emerald-500/10'
                      }`}
                      title="Utile"
                    >
                      <ThumbsUp size={13} />
                    </button>
                    <button
                      data-testid={`feedback-negative-${i}`}
                      onClick={() => submitFeedback(i, 'negative')}
                      disabled={msg.feedback !== null}
                      className={`p-1 rounded transition-colors ${
                        msg.feedback === 'negative'
                          ? 'text-red-400 bg-red-500/15'
                          : msg.feedback === 'positive'
                            ? 'text-slate-600 cursor-not-allowed'
                            : 'text-slate-500 hover:text-red-400 hover:bg-red-500/10'
                      }`}
                      title="Pas utile"
                    >
                      <ThumbsDown size={13} />
                    </button>
                    <TTSButton personId={person.person_id} text={msg.content} />
                    {msg.feedback && (
                      <span className="text-[9px] text-slate-600 ml-1">
                        {msg.feedback === 'positive' ? 'Merci !' : 'Merci, on ameliore.'}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-800 border border-slate-700/50 rounded-xl px-4 py-3">
              <Loader2 size={16} className="animate-spin text-blue-400" />
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-slate-700/50 p-3 flex gap-2">
        <input type="text" value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && sendMessage()}
          placeholder={`Parler a ${person.name}...`}
          data-testid="chat-input"
          className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
        />
        <button data-testid="chat-send" onClick={sendMessage} disabled={loading || !input.trim()}
          className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
};


// Retrieval trace — discrete info bar under each response
const RetrievalTrace = ({ info }) => {
  const [expanded, setExpanded] = useState(false);
  const intent = INTENT_LABELS[info.intent_level] || INTENT_LABELS.GENERAL;
  const sources = info.sources_breakdown || {};
  const sourceKeys = Object.keys(sources);

  return (
    <div className="ml-1 mt-0.5">
      <button
        data-testid="toggle-trace"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-[10px] text-slate-500 hover:text-slate-400 transition-colors"
      >
        <Info size={10} />
        <span className={`px-1.5 py-0.5 rounded border text-[9px] ${intent.color}`}>
          {intent.label}
        </span>
        <span>{info.context_used} souvenirs</span>
        <span className="text-slate-600">·</span>
        <span>{((info.chars_injected || 0) / 1000).toFixed(1)}k chars</span>
        <span className="text-slate-600">·</span>
        <span>{info.response_time_ms}ms</span>
        <ChevronDown size={10} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="mt-1 px-2 py-1.5 bg-slate-900/50 rounded border border-slate-800/50 text-[10px] text-slate-500 space-y-0.5">
          <div className="flex gap-3">
            <span>Mode: <strong className="text-slate-400">{info.intent_level}</strong></span>
            <span>Total memoires: <strong className="text-slate-400">{info.total_memories}</strong></span>
          </div>
          {sourceKeys.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              Sources:
              {sourceKeys.map(k => (
                <span key={k} className="text-slate-400">
                  {(SOURCE_LABELS[k]?.label) || k}: {sources[k]}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════
// TAB: DECISIONS
// ═══════════════════════════════════════
const DecisionsTab = ({ person }) => {
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await authFetch(`${API}/api/clones/persons/${person.person_id}/decisions`);
        setDecisions(data.decisions || []);
      } catch (err) {
        console.error('Load decisions error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [person.person_id]);

  const updateDecision = async (decisionId, status, reason = '') => {
    try {
      await authFetch(`${API}/api/clones/persons/${person.person_id}/decisions/${decisionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          decision_reason: reason,
          human_feedback: { rating: status === 'approved' ? 'positive' : 'negative', comment: reason, was_useful: status === 'approved' },
        }),
      });
      setDecisions(prev => prev.map(d => d.decision_id === decisionId ? { ...d, status } : d));
    } catch (err) {
      console.error('Update decision error:', err);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-blue-400" /></div>;

  if (decisions.length === 0) {
    return (
      <div data-testid="decisions-tab" className="text-center py-16 text-slate-500">
        <Shield size={32} className="mx-auto mb-3 text-slate-600" />
        <p className="text-sm font-medium">Mode observation actif</p>
        <p className="text-xs mt-1">Les suggestions apparaitront ici quand l'autonomie sera activee.</p>
        <p className="text-xs mt-3 text-slate-600">Autonomie actuelle : Niveau {person.agent_config?.autonomy_level || 0}</p>
      </div>
    );
  }

  const STATUS_COLORS = {
    pending:  'bg-amber-500/15 text-amber-400 border-amber-500/30',
    approved: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    rejected: 'bg-red-500/15 text-red-400 border-red-500/30',
    ignored:  'bg-slate-500/15 text-slate-400 border-slate-500/30',
    executed: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  };

  return (
    <div data-testid="decisions-tab" className="space-y-3">
      {decisions.map(d => (
        <div key={d.decision_id} className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded text-[10px] border ${STATUS_COLORS[d.status] || STATUS_COLORS.pending}`}>
              {d.status}
            </span>
            <span className="text-xs text-slate-500">{new Date(d.created_at).toLocaleString('fr-FR')}</span>
            <span className="text-xs text-slate-600">{d.type}</span>
          </div>
          <p className="text-sm text-slate-300">{d.action_proposed?.type}: {JSON.stringify(d.action_proposed).slice(0, 100)}</p>
          <p className="text-xs text-slate-400">{d.reasoning}</p>
          {d.status === 'pending' && (
            <div className="flex gap-2 mt-2">
              <button onClick={() => updateDecision(d.decision_id, 'approved')}
                className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded text-xs hover:bg-emerald-500"
              >
                <CheckCircle2 size={12} /> Approuver
              </button>
              <button onClick={() => updateDecision(d.decision_id, 'rejected')}
                className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded text-xs hover:bg-red-500"
              >
                <XCircle size={12} /> Refuser
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};


// ═══════════════════════════════════════
// TAB: VOICE CLONE
// ═══════════════════════════════════════
const VoiceTab = ({ person }) => {
  const [voiceConfig, setVoiceConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cloning, setCloning] = useState(false);
  const [ttsText, setTtsText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);

  const loadVoice = async () => {
    try {
      const data = await authFetch(`${API}/api/clones/persons/${person.person_id}/voice`);
      setVoiceConfig(data);
    } catch (err) {
      console.error('Load voice:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadVoice(); }, [person.person_id]);

  const createClone = async () => {
    setCloning(true);
    try {
      const data = await authFetch(`${API}/api/clones/persons/${person.person_id}/voice/clone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      await loadVoice();
    } catch (err) {
      console.error('Clone voice error:', err);
      alert('Erreur: ' + err.message);
    } finally {
      setCloning(false);
    }
  };

  const generateTTS = async () => {
    if (!ttsText.trim()) return;
    setGenerating(true);
    setAudioUrl(null);
    try {
      const resp = await fetch(`${API}/api/clones/persons/${person.person_id}/voice/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: ttsText }),
      });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
    } catch (err) {
      console.error('TTS error:', err);
      alert('Erreur TTS: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-blue-400" /></div>;

  const hasVoice = voiceConfig?.has_voice;

  return (
    <div data-testid="voice-tab" className="space-y-6">
      {/* Voice Status */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-white flex items-center gap-2">
            <Mic size={14} className={hasVoice ? 'text-emerald-400' : 'text-slate-500'} />
            Clone vocal
            {hasVoice ? (
              <span className="px-2 py-0.5 rounded text-[10px] bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">Actif</span>
            ) : (
              <span className="px-2 py-0.5 rounded text-[10px] bg-slate-600/30 text-slate-400 border border-slate-600/30">Non configure</span>
            )}
          </h4>
        </div>

        {hasVoice ? (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/30">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Provider</p>
                <p className="text-sm text-white font-medium mt-0.5">{voiceConfig.voice_config?.provider || 'ElevenLabs'}</p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/30">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Voice ID</p>
                <p className="text-xs text-slate-400 mt-0.5 font-mono truncate">{voiceConfig.voice_config?.voice_id}</p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/30">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Echantillons</p>
                <p className="text-sm text-white font-medium mt-0.5">{voiceConfig.voice_config?.samples_used || '?'}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <Mic size={28} className="mx-auto mb-3 text-slate-600" />
            <p className="text-sm text-slate-400 mb-1">Aucun clone vocal configure</p>
            <p className="text-xs text-slate-500 mb-4">Le systeme selectionnera automatiquement les meilleurs enregistrements Ringover pour creer la voix.</p>
            <button data-testid="create-voice-clone" onClick={createClone} disabled={cloning}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-500 disabled:opacity-50"
            >
              {cloning ? <Loader2 size={14} className="animate-spin" /> : <Mic size={14} />}
              {cloning ? 'Creation en cours...' : 'Creer le clone vocal'}
            </button>
          </div>
        )}
      </div>

      {/* TTS Test */}
      {hasVoice && (
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Activity size={14} className="text-blue-400" /> Test vocal
          </h4>
          <textarea
            data-testid="tts-text-input"
            value={ttsText}
            onChange={e => setTtsText(e.target.value)}
            placeholder="Tapez un texte pour entendre la voix clonee..."
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-sm text-slate-300 placeholder-slate-600 focus:border-blue-500 focus:outline-none resize-none h-20"
          />
          <div className="flex items-center gap-3 mt-3">
            <button data-testid="generate-tts" onClick={generateTTS} disabled={generating || !ttsText.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-500 disabled:opacity-50"
            >
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
              {generating ? 'Generation...' : 'Generer l\'audio'}
            </button>
            {audioUrl && (
              <audio data-testid="tts-audio-player" controls src={audioUrl} className="h-8" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════
// CLONE DETAIL VIEW
// ═══════════════════════════════════════
const CloneDetail = ({ person, onBack, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('sources');
  const [saving, setSaving] = useState(false);

  const updatePerson = async (updates) => {
    setSaving(true);
    try {
      await authFetch(`${API}/api/clones/persons/${person.person_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      onRefresh();
    } catch (err) {
      console.error('Update error:', err);
    } finally {
      setSaving(false);
    }
  };

  const TABS = [
    { id: 'sources',   label: 'Sources',    icon: Database },
    { id: 'behaviour', label: 'Behaviour',  icon: Brain },
    { id: 'timeline',  label: 'Timeline',   icon: Clock },
    { id: 'chat',      label: 'Chat',       icon: MessageSquare },
    { id: 'voice',     label: 'Voix',       icon: Mic },
    { id: 'decisions', label: 'Decisions',  icon: Shield },
  ];

  return (
    <div data-testid="clone-detail" className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button data-testid="clone-back" onClick={onBack}
          className="p-2 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-violet-500/20 border border-blue-500/30 flex items-center justify-center text-lg font-bold text-blue-400">
          {(person.prenom || person.name || '?')[0]}
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-white">{person.name}</h2>
          <p className="text-xs text-slate-400">{(person.contexts || []).map(c => c.role).join(', ')}</p>
        </div>
        {saving && <Loader2 size={16} className="animate-spin text-blue-400" />}
        <span className={`px-2 py-0.5 rounded text-[10px] border ${
          person.type === 'personnel' ? 'bg-violet-500/15 text-violet-400 border-violet-500/30' : 'bg-blue-500/15 text-blue-400 border-blue-500/30'
        }`}>
          {person.type === 'personnel' ? 'Personnel' : 'Collaborateur'}
        </span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-700">
        {TABS.map(tab => (
          <button key={tab.id} data-testid={`clone-tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'sources'   && <SourcesTab person={person} onUpdate={updatePerson} onRefresh={onRefresh} />}
      {activeTab === 'behaviour' && <BehaviourTab person={person} onUpdate={updatePerson} />}
      {activeTab === 'timeline'  && <TimelineTab person={person} />}
      {activeTab === 'chat'      && <ChatTab person={person} />}
      {activeTab === 'voice'     && <VoiceTab person={person} />}
      {activeTab === 'decisions' && <DecisionsTab person={person} />}
    </div>
  );
};


// ═══════════════════════════════════════
// CREATE PERSON MODAL
// ═══════════════════════════════════════
const CreatePersonModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [creating, setCreating] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      await authFetch(`${API}/api/clones/persons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name, prenom, nom, email, type: 'personnel',
          contexts: role ? [{ id: 'sani-adapt', role, active: true }] : [],
        }),
      });
      onCreated();
      onClose();
    } catch (err) {
      console.error('Create error:', err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-white">Nouveau clone personnel</h3>
        <p className="text-xs text-slate-400">Les clones collaborateurs sont crees automatiquement depuis Gestion des acces.</p>
        <div className="space-y-3">
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Nom complet"
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none" />
          <div className="grid grid-cols-2 gap-2">
            <input type="text" value={prenom} onChange={e => setPrenom(e.target.value)} placeholder="Prenom"
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none" />
            <input type="text" value={nom} onChange={e => setNom(e.target.value)} placeholder="Nom"
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none" />
          </div>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email"
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none" />
          <input type="text" value={role} onChange={e => setRole(e.target.value)} placeholder="Role (ex: Dirigeant, Personnel)"
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none" />
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Annuler</button>
          <button data-testid="create-person-submit" onClick={create} disabled={creating || !name.trim()}
            className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-500 disabled:opacity-50"
          >
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Creer
          </button>
        </div>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════
// MAIN: CLONE SPACE
// ═══════════════════════════════════════
const CloneSpace = () => {
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const loadPersons = useCallback(async () => {
    setLoading(true);
    try {
      const data = await authFetch(`${API}/api/clones/persons`);
      setPersons(data.persons || []);
    } catch (err) {
      console.error('Load persons error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPersons(); }, [loadPersons]);

  const refreshSelected = async () => {
    if (!selectedPerson) return;
    try {
      const data = await authFetch(`${API}/api/clones/persons/${selectedPerson.person_id}`);
      setSelectedPerson(data);
      loadPersons();
    } catch (err) {
      console.error('Refresh error:', err);
    }
  };

  if (selectedPerson) {
    return <CloneDetail person={selectedPerson} onBack={() => setSelectedPerson(null)} onRefresh={refreshSelected} />;
  }

  return (
    <div data-testid="clone-space" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Brain size={22} className="text-blue-400" />
            Clones Numeriques
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Intelligence personnelle — memoire, comprehension, suggestions
          </p>
        </div>
        <button data-testid="create-clone-btn" onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm hover:bg-violet-500"
        >
          <Plus size={16} /> Clone personnel
        </button>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Clones actifs', value: persons.filter(p => p.clonable).length, color: 'text-emerald-400' },
          { label: 'Collaborateurs', value: persons.filter(p => p.type === 'collaborateur').length, color: 'text-blue-400' },
          { label: 'Personnels', value: persons.filter(p => p.type === 'personnel').length, color: 'text-violet-400' },
          { label: 'Total memoires', value: persons.reduce((s, p) => s + (p.stats?.total_memories || 0), 0), color: 'text-amber-400' },
        ].map((stat, i) => (
          <div key={i} className="bg-slate-800/40 border border-slate-700/40 rounded-lg px-4 py-3">
            <p className={`text-lg font-bold ${stat.color}`}>{stat.value}</p>
            <p className="text-[10px] text-slate-500">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-blue-400" /></div>
      )}

      {/* Person cards */}
      {!loading && persons.length === 0 && (
        <div className="text-center py-16 text-slate-500">
          <Brain size={40} className="mx-auto mb-4 text-slate-600" />
          <p className="text-base">Aucun clone actif</p>
          <p className="text-xs mt-1">Activez le clonage depuis <strong className="text-blue-400">Gestion des acces</strong> sur le profil d'un collaborateur,</p>
          <p className="text-xs">ou creez un clone personnel avec le bouton ci-dessus.</p>
        </div>
      )}

      {!loading && persons.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {persons.map(p => (
            <PersonCard key={p.person_id} person={p} onClick={setSelectedPerson} />
          ))}
        </div>
      )}

      {showCreate && <CreatePersonModal onClose={() => setShowCreate(false)} onCreated={loadPersons} />}
    </div>
  );
};

export default CloneSpace;
